import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import FiltersField from '@afosto/app-components-react/FiltersField';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import camelize from '@afosto/utils/string/camelize';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { pricing as pricingApi } from '@afosto/order/api/Pricing';
import translations from './translations';

const ConstraintsFormSection = () => {
  const intl = useIntl();
  const [availableOperators, setAvailableOperators] = useState({});
  const [constraintOptions, setConstraintOptions] = useState([]);
  const [defaultOperators] = useState([
    {
      label: intl.formatMessage(translations.operators.in),
      value: 'in',
    },
    {
      label: intl.formatMessage(translations.operators.gte),
      value: 'gte',
    },
    {
      label: intl.formatMessage(translations.operators.gt),
      value: 'gt',
    },
    {
      label: intl.formatMessage(translations.operators.lte),
      value: 'lte',
    },
    {
      label: intl.formatMessage(translations.operators.lt),
      value: 'lt',
    },
  ]);
  const [isFetchingOperators, setIsFetchingOperators] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchConstraintOptions = async () => {
      setIsFetchingOperators(true);

      try {
        const response = await pricingApi.getConstraintOptions();
        const { data: constraints = {} } = response;
        const options = Object.keys(constraints).reduce((acc, constraintKey) => {
          const formattedKey = camelize(constraintKey);

          return [
            ...acc,
            {
              label: intl.formatMessage({
                id: `pricingRuleForm.constraintTypes.${formattedKey}`,
                defaultMessage: formattedKey,
                description: `Label for constraint type ${constraintKey}`,
              }),
              value: constraintKey,
            },
          ];
        }, []);

        if (isMounted) {
          setConstraintOptions(sortArrayByProperty(options, 'label'));
          setAvailableOperators(constraints);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableOperators({});
        }
      } finally {
        if (isMounted) {
          setIsFetchingOperators(false);
        }
      }
    };

    fetchConstraintOptions().catch(() => {
      // Catch only.
    });

    return () => {
      isMounted = false;
    };
  }, [intl]);

  const handleFetchOperators = async key => {
    try {
      const operators = availableOperators[key];

      if (operators) {
        return defaultOperators.filter(constraintOperator =>
          operators.includes(constraintOperator.value),
        );
      }

      return defaultOperators;
    } catch (err) {
      return defaultOperators;
    }
  };

  const handleFetchFormat = key => {
    if (key === 'quantity') {
      return 'number';
    }

    if (key === 'amount') {
      return 'currency';
    }

    if (key === 'client') {
      return 'client';
    }

    if (key === 'coupon') {
      return 'couponCategory';
    }

    if (key === 'filtered_amount') {
      return 'currency';
    }

    if (key === 'payment_method') {
      return 'paymentMethod';
    }

    if (key === 'shipping_method') {
      return 'shippingMethod';
    }

    return null;
  };

  return (
    <Card style={{ marginBottom: 8 }}>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent>
        <LoadingWrapper isLoading={isFetchingOperators}>
          <FiltersField
            name="constraints"
            showLabel={false}
            filterOptions={constraintOptions}
            onFetchOperators={handleFetchOperators}
            onFetchFormat={handleFetchFormat}
          />
        </LoadingWrapper>
      </CardContent>
    </Card>
  );
};

export default ConstraintsFormSection;
